import { useRef } from "react";
import type { KeyboardEvent } from "react";
import type { TileFilterFieldType } from "@fortex/api";
import { activeFilterValues, minimumTermLength, searchLabelOf } from "../../core/search";
import type { ResultsState } from "../../core/store";
import type { Labels } from "../../i18n/labels";
import { useLabels, useLayers, useMapCore, useMapStore } from "../hooks";
import { Field } from "../primitives/Field";
import { Input } from "../primitives/Input";

export type PanelSearchProps = { autoFocus?: boolean; className?: string };

export function searchStatus(term: string, results: ResultsState, labels: Labels, filtered = false): string | null {
  const trimmed = term.trim();

  if (!filtered && trimmed.length > 0 && trimmed.length < minimumTermLength) return labels.searchMinimum;
  if (results.source !== "search") return null;
  if (results.loading) return labels.loading;
  if (results.error !== null) return `${labels.searchFailed}: ${results.error}`;
  if (results.items.length === 0 && (filtered || trimmed.length >= minimumTermLength)) return labels.searchNoResults;

  return null;
}

export function filterInputType(type: TileFilterFieldType): "text" | "date" {
  return type === "Date" ? "date" : "text";
}

export function PanelSearch({ autoFocus = false, className }: PanelSearchProps) {
  const core = useMapCore();
  const labels = useLabels();
  const term = useMapStore(a => a.search.term);
  const results = useMapStore(a => a.results);
  const filterFields = useMapStore(a => a.search.filterFields);
  const filters = useMapStore(a => a.search.filters);
  const inputRef = useRef<HTMLInputElement>(null);
  const status = searchStatus(term, results, labels, Object.keys(activeFilterValues(filterFields, filters)).length > 0);
  const { list } = useLayers();
  const searchOptions = core.options.controls.search;
  const title = searchLabelOf(
    list.map(snapshot => {
      const entry = core.layers.entry(snapshot.id);

      return { selected: snapshot.selected, kind: entry?.kind ?? snapshot.kind, idName: entry?.idName ?? snapshot.key, searchLabel: typeof entry?.settings.searchLabel === "string" ? entry.settings.searchLabel : null };
    }),
    searchOptions.layers,
    searchOptions.label
  );

  const clear = () => {
    core.search.clear();
    inputRef.current?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape" && term.length > 0) {
      event.preventDefault();
      clear();
    }
  };

  return (
    <div className={["fxmap-panel-search", className ?? ""].filter(Boolean).join(" ")} role="search" data-fxmap="panel-search">
      {title !== null ? (
        <div className="fxmap-panel-search-title" data-fxmap="panel-search-title">
          {title}
        </div>
      ) : null}
      <Input ref={inputRef} type="search" icon="search" autoFocus={autoFocus} autoComplete="off" placeholder={labels.searchPlaceholder} aria-label={title ?? labels.searchPlaceholder} value={term} onChange={event => core.search.setTerm(event.target.value)} onKeyDown={onKeyDown} onClear={clear} clearLabel={labels.close} className="fxmap-panel-search-input" />
      {filterFields.length > 0 ? (
        <div className="fxmap-panel-search-filters" data-fxmap="panel-search-filters">
          {filterFields.map(field => (
            <Field key={`${field.layer}:${field.key}`} label={field.humanName} className="fxmap-panel-search-filter">
              <Input
                type={filterInputType(field.type)}
                inputMode={field.type === "Number" ? "numeric" : undefined}
                autoComplete="off"
                value={filters[field.layer]?.[field.key] ?? ""}
                onChange={event => core.search.setFilter(field.layer, field.key, event.target.value)}
                onClear={field.type === "Date" ? null : () => core.search.setFilter(field.layer, field.key, "")}
                clearLabel={labels.close}
                className="fxmap-panel-search-input"
                data-fxmap-filter={`${field.layer}:${field.key}`}
              />
            </Field>
          ))}
        </div>
      ) : null}
      {status !== null ? (
        <div className="fxmap-panel-search-status fxmap-muted" role="status">
          {status}
        </div>
      ) : null}
    </div>
  );
}
